/**
 * screens/board.tsx — Now Serving public display (door board).
 * Full-bleed dark board, aspect 1.78, white text. NOW SERVING cards
 * (token + first name + table) and an UP NEXT rail. First-name only for privacy.
 * Public surface: no chrome, no auth.
 */

import React from 'react';
import { View, Text, ScrollView, useWindowDimensions } from 'react-native';
import { useQueue } from '../queue/store';
import { waitingFIFO } from '../queue/engine';
import { fmtWait, waitMin, firstName, clockHMS, dateLong } from '../queue/format';
import { Mono, SkMark } from '../queue/components/atoms';
import { useGuestSession } from '../queue/guestSession';
import { useNowServing } from '../screens/hooks/useNowServing';

/** Display rows the board renders — produced identically from live or in-memory. */
interface ServeRow { id: string; token: string; name: string | null; table: string | null }
interface NextRow { id: string; name: string | null; party: number | null; position: number | null }

export default function BoardScreen() {
  const { state } = useQueue();
  const { width } = useWindowDimensions();
  const { isPublic, queueId, queueInfo, authHeader } = useGuestSession();
  // A door TV is wide; a phone is not. Below this we drop the fixed 16:9 board
  // and stack everything vertically so the board is fully readable on a phone
  // (nothing removed — it reflows).
  const narrow = width < 760;

  // Public door board reads live via the guest token; otherwise the in-memory
  // demo (e.g. the admin preview of this surface).
  const useLive = isPublic && !!queueId;
  const { view } = useNowServing(useLive ? queueId : null, authHeader);

  let queueName: string;
  let serving: ServeRow[];
  let waiting: NextRow[];

  if (useLive && view) {
    queueName = view.queue.name;
    serving = view.nowServing.map((s, i) => ({ id: `${s.token}-${i}`, token: s.token, name: s.firstName, table: s.table }));
    // Live up-next is position + party only (no names, by privacy design).
    waiting = view.upNext.map((u, i) => ({ id: `up-${i}`, name: null, party: u.partySize, position: u.position }));
  } else {
    queueName = state.queueName;
    serving = state.entries
      .filter(e => e.state === 'CALLED' || e.state === 'ALLOCATED')
      .sort((a, b) => a.joinAt - b.joinAt)
      .map(e => ({ id: e.id, token: e.token, name: firstName(e.displayName), table: e.allocatedResource }));
    waiting = waitingFIFO(state.entries).map(e => ({ id: e.id, name: firstName(e.displayName), party: e.party, position: null }));
  }
  void queueInfo; // available for paused/closed states; board shows the line regardless

  return (
    <ScrollView contentContainerStyle={{ flexGrow: 1, alignItems: 'center', justifyContent: narrow ? 'flex-start' : 'center', padding: narrow ? 12 : 24, backgroundColor: '#E9E8E3' }}>
      <View style={{ width: '100%', maxWidth: 1240 }}>
        <View style={{
          backgroundColor: '#08090A', borderRadius: 18, overflow: 'hidden', flexDirection: 'column',
          // Wide → cinematic 16:9 board. Narrow → natural height, fills the phone.
          ...(narrow ? { minHeight: 560 } : { aspectRatio: 1.78 }),
        }}>
          {/* header */}
          <View style={{
            flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
            paddingHorizontal: narrow ? 18 : 38, paddingVertical: narrow ? 18 : 26, borderBottomWidth: 1, borderBottomColor: '#1C1D1F',
          }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: narrow ? 10 : 14, flex: 1, minWidth: 0 }}>
              <SkMark size={narrow ? 28 : 34} color="#FFFFFF" />
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text numberOfLines={1} style={{ fontSize: narrow ? 16 : 20, fontWeight: '700', color: '#FFFFFF' }}>{queueName}</Text>
                <Text style={{ fontSize: narrow ? 11 : 13, color: '#6C6C70' }}>{dateLong(state.now)}</Text>
              </View>
            </View>
            <View style={{ alignItems: 'flex-end', flexShrink: 0, paddingLeft: 10 }}>
              <Mono style={{ fontSize: narrow ? 22 : 34, fontWeight: '600', color: '#FFFFFF', lineHeight: narrow ? 24 : 34 }}>{clockHMS(state.now)}</Mono>
              <Text style={{ fontSize: narrow ? 9 : 12, color: '#6C6C70', letterSpacing: 1 }}>LIVE · AUTO-REFRESH</Text>
            </View>
          </View>

          <View style={{ flex: 1, flexDirection: narrow ? 'column' : 'row', minHeight: 0 }}>
            {/* now serving */}
            <View style={{ flex: narrow ? undefined : 2.1, paddingHorizontal: narrow ? 18 : 38, paddingVertical: narrow ? 22 : 30 }}>
              <Mono style={{ fontSize: narrow ? 12 : 14, letterSpacing: 3.5, color: '#6C6C70' }}>NOW SERVING</Mono>
              <View style={{ flex: 1, justifyContent: 'center', marginTop: narrow ? 14 : 0 }}>
                {serving.length === 0 ? (
                  <Text style={{ color: '#3D3D40', fontSize: narrow ? 18 : 26, fontWeight: '600' }}>No tables ready right now</Text>
                ) : (
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: narrow ? 12 : 20 }}>
                    {serving.map(b => (
                      <View key={b.id} style={{
                        width: narrow ? '100%' : '47%', borderWidth: 1, borderColor: '#2A2B2D', borderRadius: 16, padding: narrow ? 16 : 22,
                        flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
                      }}>
                        <View style={{ flex: 1, minWidth: 0 }}>
                          <Mono style={{ fontSize: narrow ? 13 : 15, color: '#6C6C70' }}>#{b.token}</Mono>
                          <Text numberOfLines={1} style={{ fontSize: narrow ? 28 : 34, fontWeight: '700', lineHeight: narrow ? 32 : 36, marginTop: 2, color: '#FFFFFF' }}>{b.name}</Text>
                        </View>
                        <View style={{ alignItems: 'center', backgroundColor: '#FFFFFF', borderRadius: 14, paddingHorizontal: narrow ? 16 : 22, paddingVertical: narrow ? 10 : 14, minWidth: narrow ? 80 : 96, flexShrink: 0 }}>
                          <Text style={{ fontSize: 10, letterSpacing: 1.8, color: '#6C6C70', fontWeight: '600' }}>TABLE</Text>
                          <Mono style={{ fontSize: narrow ? 34 : 42, fontWeight: '600', lineHeight: narrow ? 36 : 42, color: '#08090A' }}>{b.table || '—'}</Mono>
                        </View>
                      </View>
                    ))}
                  </View>
                )}
              </View>
            </View>

            {/* up next */}
            <View style={{
              flex: narrow ? undefined : 1, backgroundColor: '#0B0C0D',
              paddingHorizontal: narrow ? 18 : 30, paddingVertical: narrow ? 22 : 30,
              ...(narrow
                ? { borderTopWidth: 1, borderTopColor: '#1C1D1F' }
                : { borderLeftWidth: 1, borderLeftColor: '#1C1D1F' }),
            }}>
              <View style={{ flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <Mono style={{ fontSize: 14, letterSpacing: 3, color: '#6C6C70' }}>UP NEXT</Mono>
                <Text style={{ fontSize: 12, color: '#6C6C70' }}>
                  <Mono style={{ color: '#FFFFFF', fontSize: 15 }}>{waiting.length}</Mono> waiting
                </Text>
              </View>
              <View style={{ marginTop: 18, flex: 1 }}>
                {waiting.slice(0, 6).map((n, i) => (
                  <View key={n.id} style={{
                    flexDirection: 'row', alignItems: 'center', gap: 14, paddingVertical: 13, paddingHorizontal: 4,
                    borderBottomWidth: 1, borderBottomColor: '#18191B',
                  }}>
                    <Mono style={{ fontSize: 18, fontWeight: '600', color: '#6C6C70', width: 30 }}>{String(n.position ?? i + 1).padStart(2, '0')}</Mono>
                    {/* Public board shows position + party only (no names — privacy). */}
                    <Text style={{ flex: 1, fontSize: 18, fontWeight: '600', color: '#FFFFFF' }}>{n.name ?? `Party of ${n.party ?? '—'}`}</Text>
                    <Mono style={{ fontSize: 14, color: '#9B9B9F' }}>×{n.party}</Mono>
                  </View>
                ))}
              </View>
              <View style={{ borderTopWidth: 1, borderTopColor: '#1C1D1F', paddingTop: 16 }}>
                <Text style={{ fontSize: 13, color: '#6C6C70' }}>
                  Est. wait to join now · <Mono style={{ color: '#FFFFFF' }}>{fmtWait(waitMin(waiting.length))}</Mono>
                </Text>
              </View>
            </View>
          </View>
        </View>
        <Mono style={{ textAlign: 'center', marginTop: 12, fontSize: 11, color: '#6C6C70' }}>
          Read-only public display · scan the QR at the door to join
        </Mono>
      </View>
    </ScrollView>
  );
}
